import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const PLATFORMS = [
  { name: 'Cloud', href: 'https://admin-cloud.devskin.com', color: '#0ea5e9' },
  { name: 'Marketplace', href: 'https://admin-marketplace.devskin.com', color: '#16a34a' },
  { name: 'Marketing', href: 'https://admin-marketing.devskin.com', color: '#f97316' },
  { name: 'CRM', href: 'https://admin-crm.devskin.com', color: '#ef4444' },
  { name: 'Analytics', href: 'https://admin-flux.devskin.com', color: '#3b82f6' },
  { name: 'Digital Sign', href: 'https://admin-sign.devskin.com', color: '#8b5cf6' },
];

export default function PlatformDots({ current }: { current: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="min-w-[44px] min-h-[44px] p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center justify-center"
        title={t('platforms.title', 'Platforms')}
      >
        <div className="flex items-center gap-0.5 sm:gap-1">
          {PLATFORMS.map((p) => {
            const isCurrent = p.name.toLowerCase() === current;
            return (
              <span
                key={p.name}
                className="rounded-full transition-all duration-200"
                style={{
                  width: isCurrent ? 10 : 6,
                  height: isCurrent ? 10 : 6,
                  backgroundColor: p.color,
                  opacity: isCurrent ? 1 : 0.5,
                  boxShadow: isCurrent ? `0 0 0 2px white, 0 0 0 3px ${p.color}` : 'none',
                }}
              />
            );
          })}
        </div>
      </button>

      {open && (
        <div
          className="fixed sm:absolute right-4 sm:right-0 top-auto sm:top-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-[60] w-[calc(100vw-2rem)] sm:w-56 max-h-[80vh] overflow-y-auto py-2"
          style={{ animation: 'fadeIn 0.15s ease' }}
        >
          <div className="px-3 py-1.5 mb-1">
            <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{t('platforms.title', 'Platforms')}</span>
          </div>
          {PLATFORMS.map((p) => {
            const isCurrent = p.name.toLowerCase() === current;
            return (
              <a
                key={p.name}
                href={isCurrent ? '#' : p.href}
                target={isCurrent ? undefined : '_blank'}
                rel={isCurrent ? undefined : 'noopener noreferrer'}
                onClick={isCurrent ? (e) => { e.preventDefault(); setOpen(false); } : undefined}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                  isCurrent
                    ? 'bg-gray-50 dark:bg-white/[0.06] font-medium text-gray-900 dark:text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04]'
                }`}
              >
                <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
                <span className="flex-1">{p.name}</span>
                {isCurrent && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400">{t('platforms.current', 'current')}</span>
                )}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
